import Cookies from 'js-cookie';
import { useNavigate } from 'react-router-dom';
import { useEffect } from 'react';
import { useAuthentication } from '@/hooks/useAuthentication';
import Routes from './router/routes';

export default function App() {
  const navigate = useNavigate();
  // skipAutoRedirect: true so the hook's own auto-redirect useEffect doesn't
  // race with us, and so processAuthSuccess doesn't navigate internally —
  // App.tsx owns navigation in this flow.
  const { processAuthSuccess } = useAuthentication({ skipAutoRedirect: true });
  const token = Cookies.get('token');
  const main_organization = Cookies.get('main_organization');
  const main_workspace = Cookies.get('fallback_workspace');

  useEffect(() => {
    // Fast path: all cookies present → navigate directly.
    if (token && main_organization && main_workspace) {
      navigate(
        `${Routes.organization.path}/${main_organization}${Routes.workspace.path}/${main_workspace}`,
      );
      return;
    }
    // No token → straight to login.
    if (!token) {
      navigate(Routes.login.path);
      return;
    }
    // TELV1-278: Token cookie present but workspace context cookie missing.
    // Previously this fell through to `return null` → blank screen. Re-resolve
    // via the existing login flow: fetches user orgs, finds a valid one,
    // computes mainWorkspace, re-writes the missing cookies, and returns the
    // resolved org/workspace IDs. We navigate based on the result.
    // Falls back to /login if re-resolve fails (invalid token, no valid org).
    //
    // TELV1-296 follow-up — on failure, ALSO clear the token cookie before
    // navigating to /login. Previously the cookie stayed, so Login's
    // auto-redirect useEffect saw `initialAuthTokenRef.current` truthy on
    // mount and bounced the user straight back to '/', which re-ran this
    // path → infinite redirect loop. Clearing the cookie ensures /login
    // mounts cleanly and the user can re-authenticate.
    processAuthSuccess(token).then((result) => {
      if (result.success && result.currentOrganization && result.currentWorkspace) {
        navigate(
          `${Routes.organization.path}/${result.currentOrganization}${Routes.workspace.path}/${result.currentWorkspace}`,
        );
      } else {
        Cookies.remove('token');
        Cookies.remove('main_organization');
        Cookies.remove('fallback_workspace');
        Cookies.remove('remember_me');
        navigate(Routes.login.path);
      }
    });
  }, [navigate, token, main_organization, main_workspace, processAuthSuccess]);

  return null;
}
